import React, { useState, useEffect } from "react";
import { SubService, SubServiceFormData } from "../../../types";
import { Modal, FormActions } from "../../shared";

interface CreateSubServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: SubServiceFormData) => Promise<void>;
  editingSubService?: SubService | null;
  loading?: boolean;
}

export const CreateSubServiceModal: React.FC<CreateSubServiceModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  editingSubService,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    expirationDays: "",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (editingSubService) {
      setFormData({
        name: editingSubService.name,
        description: editingSubService.description || "",
        expirationDays:
          editingSubService.expirationDays != null
            ? String(editingSubService.expirationDays)
            : "",
      });
    } else {
      setFormData({
        name: "",
        description: "",
        expirationDays: "",
      });
    }
    setErrors({});
  }, [editingSubService, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = "Nome é obrigatório";
    } else if (formData.name.length < 2) {
      newErrors.name = "Nome deve ter pelo menos 2 caracteres";
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = "Descrição deve ter no máximo 500 caracteres";
    }

    const expirationTrimmed = formData.expirationDays.trim();
    if (expirationTrimmed !== "") {
      const days = parseInt(expirationTrimmed, 10);
      if (isNaN(days) || days <= 0) {
        newErrors.expirationDays =
          "Validade deve ser um número de dias maior que zero";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const expirationTrimmed = formData.expirationDays.trim();
      const payload: SubServiceFormData = {
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined,
        expirationDays:
          expirationTrimmed === "" ? null : parseInt(expirationTrimmed, 10),
      };
      await onSubmit(payload);
      onClose();
    } catch (error) {
      console.error("Error submitting subservice:", error);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editingSubService ? "Editar Sub-serviço" : "Novo Sub-serviço"}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name Field */}
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
          >
            Nome *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Serviço extra"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
              errors.name
                ? "border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/40"
                : "border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100"
            }`}
            disabled={loading}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        {/* Description Field */}
        <div>
          <label
            htmlFor="description"
            className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
          >
            Descrição
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Descrição detalhada do sub-serviço (opcional)"
            rows={4}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
              errors.description
                ? "border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/40"
                : "border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100"
            }`}
            disabled={loading}
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description}</p>
          )}
          <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
            {formData.description?.length || 0}/500 caracteres
          </p>
        </div>

        <div>
          <label
            htmlFor="expirationDays"
            className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
          >
            Validade (dias)
          </label>
          <input
            type="number"
            id="expirationDays"
            name="expirationDays"
            value={formData.expirationDays}
            onChange={handleInputChange}
            min="1"
            placeholder="Opcional — ex.: 30"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
              errors.expirationDays
                ? "border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/40"
                : "border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100"
            }`}
            disabled={loading}
          />
          {errors.expirationDays && (
            <p className="mt-1 text-sm text-red-600">{errors.expirationDays}</p>
          )}
          <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
            Prazo em dias para retorno do cliente após o último atendimento
            deste sub-serviço.
          </p>
        </div>

        <FormActions
          onCancel={onClose}
          onSubmit={() => {}}
          loading={loading}
          isEditing={!!editingSubService}
          submitText={editingSubService ? "Salvar Alterações" : "Criar Sub-serviço"}
          submitType="submit"
        />
      </form>
    </Modal>
  );
};

export default CreateSubServiceModal;
